import React, { useEffect, useState } from "react";
import { Box, Button, HStack, Progress, Text, VStack } from "@chakra-ui/react";
import type { RunnerState } from "../flow/engine/useFlowRunner";
import { stepById } from "../flow/flowGraph";
import { formatDuration, formatEstimateRange, type TimeEstimate } from "../flow/estimateTime";
import { describeSnapshot } from "../flow/engine/orderState";

interface Props {
  state: RunnerState;
  isBusy: boolean;
  canExecute: boolean;
  blockers: string[];
  estimate: TimeEstimate;
  targetLabel: string;
  onExecute: () => void;
  onCancel: () => void;
  onReset: () => void;
}

const ExecuteBar: React.FC<Props> = ({ state, isBusy, canExecute, blockers, estimate, targetLabel, onExecute, onCancel, onReset }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isBusy) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [isBusy]);

  const elapsed = state.startedAt ? (state.endedAt ?? (isBusy ? now : state.endedAt ?? now)) - state.startedAt : 0;
  const planned = state.plannedStepIds.length + state.branchStepIds.length;
  const done = Object.values(state.runs).filter((r) => r.status === "completed" || r.status === "skipped").length;
  const progress = planned ? (done / planned) * 100 : 0;
  const current = state.currentStepId ? stepById(state.currentStepId) : undefined;
  const currentRun = state.currentStepId ? state.runs[state.currentStepId] : undefined;
  const idle = state.status === "idle";

  return (
    <Box bg="white" borderWidth="1px" borderColor={isBusy ? "blue.300" : "gray.200"} borderRadius="lg" p={4} transition="border-color 0.3s" boxShadow={isBusy ? "sm" : undefined}>
      <HStack justify="space-between" align="flex-start" spacing={6}>
        <VStack align="stretch" spacing={1} flex={1} minW={0}>
          {idle ? (
            <>
              <Text fontSize="sm" fontWeight="700">
                Execute → {targetLabel}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Estimated {formatEstimateRange(estimate)} · {estimate.apiSteps} API steps · {estimate.eventSteps} event waits
                {estimate.skippedSteps ? ` · ${estimate.skippedSteps} will skip` : ""}
                {estimate.eventSteps ? " · event timing depends on backend triggers" : ""}
              </Text>
              {blockers.length > 0 && (
                <Text fontSize="xs" color="red.600">
                  {blockers.join(" · ")}
                </Text>
              )}
            </>
          ) : (
            <>
              <HStack spacing={3}>
                <Text fontSize="sm" fontWeight="700">
                  {state.status === "running" && (currentRun?.status === "waiting" ? "Waiting for event" : "Running")}
                  {state.status === "awaitingDecision" && "Awaiting your decision"}
                  {state.status === "completed" && (state.targetReached ? "Target reached" : "Completed")}
                  {state.status === "failed" && "Flow halted"}
                  {state.status === "cancelled" && "Cancelled"}
                </Text>
                {current && (
                  <Text fontSize="xs" color="gray.600" noOfLines={1}>
                    · {current.name}
                  </Text>
                )}
              </HStack>
              <Progress value={progress} size="xs" colorScheme={state.status === "failed" ? "red" : state.status === "completed" ? "green" : "blue"} borderRadius="full" isIndeterminate={false} sx={{ "& > div": { transition: "width 0.4s ease" } }} />
              <HStack fontSize="xs" color="gray.500" spacing={4} wrap="wrap">
                <Text>
                  {done}/{planned} steps
                </Text>
                <Text>elapsed {formatDuration(elapsed)}</Text>
                {isBusy && <Text>remaining {formatEstimateRange(estimate)}</Text>}
                <Text>{state.totalApiCalls} API calls</Text>
                <Text>{state.totalPollChecks} state checks</Text>
              </HStack>
              <Text fontSize="xs" color="gray.600" fontFamily="mono" noOfLines={1}>
                state: {describeSnapshot(state.snapshot)}
              </Text>
            </>
          )}
        </VStack>
        <HStack flexShrink={0}>
          {isBusy ? (
            <Button size="sm" variant="outline" colorScheme="red" onClick={onCancel}>
              Stop
            </Button>
          ) : idle ? (
            <Button size="md" colorScheme="blue" onClick={onExecute} isDisabled={!canExecute} px={8}>
              Execute
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={onReset}>
              New run
            </Button>
          )}
        </HStack>
      </HStack>
    </Box>
  );
};

export default ExecuteBar;
